import { RotateCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ConfidenceTier } from '@/reactQuery/comparison';

interface ConfidenceTierBadgeProps {
  /** The confidence tier derived server-side. Undefined means a legacy doc
   *  with no tier field — renders grey "Tier unknown" + Recompute button. */
  tier?: ConfidenceTier;
  /** Segment sample sizes for the N={a}+{b} sub-label. */
  sampleSizes?: { a: number; b: number };
  /** Called when the user clicks the Recompute button (only shown for
   *  undefined-tier / legacy documents). */
  onRecompute?: () => void;
  /** True while the recompute mutation is in-flight — spins the icon. */
  isRecomputing?: boolean;
}

interface TierConfig {
  label: string;
  pillClass: string;
  dotClass: string;
}

const TIER_CONFIG: Record<ConfidenceTier, TierConfig> = {
  established: {
    label: 'Established',
    pillClass: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    dotClass: 'bg-emerald-500',
  },
  indicative: {
    label: 'Indicative',
    pillClass: 'bg-amber-50 border-amber-200 text-amber-800',
    dotClass: 'bg-amber-500',
  },
  directional: {
    label: 'Directional',
    pillClass: 'bg-red-50 border-red-200 text-red-800',
    dotClass: 'bg-red-500',
  },
};

/**
 * Pill-style badge that surfaces the sample-size confidence tier on comparison
 * artifacts. Three colour states (green/amber/red) plus a grey "Tier unknown"
 * state for legacy documents that pre-date server-side tier derivation.
 *
 * Used in both the RA chat ArtifactPanel and the Compare-tab ComparisonReport.
 */
function ConfidenceTierBadge({
  tier,
  sampleSizes,
  onRecompute,
  isRecomputing = false,
}: ConfidenceTierBadgeProps) {
  if (tier === undefined) {
    // Legacy document — no tier field. Render grey pill + recompute affordance.
    return (
      <div className="inline-flex items-center gap-2 flex-wrap">
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium bg-neutral-50 border-neutral-200 text-neutral-600"
          data-testid="confidence-tier-badge-unknown"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 shrink-0" />
          Tier unknown
        </span>
        {onRecompute && (
          <button
            type="button"
            onClick={onRecompute}
            disabled={isRecomputing}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-600 border border-neutral-200 rounded-md hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            aria-label="Recompute confidence tier"
            data-testid="confidence-tier-recompute-btn"
          >
            <RotateCw className={cn('h-3 w-3', isRecomputing && 'animate-spin')} />
            {isRecomputing ? 'Recomputing…' : 'Recompute'}
          </button>
        )}
      </div>
    );
  }

  const config = TIER_CONFIG[tier];
  const nLabel = sampleSizes !== undefined ? ` (N=${sampleSizes.a}+${sampleSizes.b})` : '';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium',
        config.pillClass,
      )}
      data-testid={`confidence-tier-badge-${tier}`}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', config.dotClass)} />
      {config.label}
      {nLabel}
    </span>
  );
}

export default ConfidenceTierBadge;
